import { useState } from "react";
import { showSuccess, showError } from "@/lib/toastHelpers";
import { useBatchInvite } from "@/hooks/useTeam";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

// ─── Types ────────────────────────────────────────────────────────────────────

interface BatchInviteDialogProps {
  /** Controls open state from parent — no internal DialogTrigger */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** workspaceId used in the API URL */
  workspaceId: string;
  roles: Array<{ _id: string; name: string }>;
  onSuccess?: () => void;
}

interface BatchResult {
  created: Array<{ email: string; invitationId?: string }>;
  skipped: Array<{ email: string; reason: string }>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BatchInviteDialog({
  open,
  onOpenChange,
  workspaceId,
  roles,
  onSuccess,
}: BatchInviteDialogProps) {
  const batchInvite = useBatchInvite();

  const [selectedRoleId, setSelectedRoleId] = useState<string>("");
  const [emailsText, setEmailsText]         = useState("");
  const [showResults, setShowResults]       = useState(false);
  const [results, setResults]               = useState<BatchResult | null>(null);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Parse textarea — split on newlines, commas, or semicolons */
  const parseEmails = (text: string): string[] =>
    text
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

  const resetForm = () => {
    setEmailsText("");
    setSelectedRoleId("");
    setShowResults(false);
    setResults(null);
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedRoleId) {
      showError("Please select a role.", "Missing role");
      return;
    }

    const emails = parseEmails(emailsText);
    if (emails.length === 0) {
      showError("Enter at least one email address.", "No emails");
      return;
    }

    const roleName = roles.find((r) => r._id === selectedRoleId)?.name ?? "member";

    try {
      // Payload matches backend: { workspaceId (URL param), emails: string[], role: string }
      const response = (await batchInvite.mutateAsync({
        workspaceId,
        emails,
        role: roleName,
      })) as BatchResult;

      setResults(response);
      setShowResults(true);

      if (response.created.length > 0) {
        showSuccess(
          `${response.created.length} invitation(s) sent.` +
            (response.skipped.length ? ` ${response.skipped.length} skipped.` : ""),
          "Batch invites sent"
        );
      } else if (response.skipped.length > 0) {
        showError(
          `All ${response.skipped.length} email(s) were skipped. Check details below.`,
          "Nothing sent"
        );
      }

      onSuccess?.();
    } catch (error: any) {
      showError(error.message ?? "Failed to send batch invitations.", "Error");
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    // Controlled by parent — no DialogTrigger here to avoid open-state conflict
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Send Batch Invitations</DialogTitle>
          <DialogDescription>
            Paste email addresses separated by commas, semicolons, or new lines.
          </DialogDescription>
        </DialogHeader>

        {showResults && results ? (
          <ResultsView
            results={results}
            onSendMore={() => setShowResults(false)}
            onDone={handleClose}
          />
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Role picker */}
            <div>
              <Label>Role *</Label>
              <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {roles.map((role) => (
                    <SelectItem key={role._id} value={role._id}>
                      {role.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Emails textarea */}
            <div>
              <Label>Email Addresses *</Label>
              <Textarea
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                placeholder={"alice@example.com\nbob@example.com\ncarol@example.com"}
                className="font-mono text-sm"
                rows={8}
              />
              <p className="text-xs text-muted-foreground mt-1">
                One per line, or separated by commas / semicolons
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={batchInvite.isPending}>
                {batchInvite.isPending ? "Sending…" : "Send Invitations"}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── ResultsView (pure display — no mutation logic) ───────────────────────────

interface ResultsViewProps {
  results: BatchResult;
  onSendMore: () => void;
  onDone: () => void;
}

function ResultsView({ results, onSendMore, onDone }: ResultsViewProps) {
  return (
    <div className="space-y-4">
      {results.created.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <CardTitle className="text-green-900 text-base">
                {results.created.length} Invitation(s) Sent
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {results.created.map((item) => (
                <li key={item.email} className="text-sm text-green-800">
                  ✓ {item.email}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {results.skipped.length > 0 && (
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <CardTitle className="text-amber-900 text-base">
                {results.skipped.length} Skipped
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1">
              {results.skipped.map((item) => (
                <li key={item.email} className="text-sm text-amber-800">
                  ✗ {item.email} — {item.reason}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2 justify-end">
        <Button variant="outline" onClick={onSendMore}>
          Send More
        </Button>
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  );
}
